'use client'

import React, { useEffect, useState, type JSX } from 'react'
import { useResponsive } from '../hooks'

import { Breakpoints } from '@dustin-web/microsite-config'
import useUniqueId from '../hooks/use-unique-id'
import isEqual from 'lodash/isEqual'

import TabordionDropDown from './tabordion-dropdown'
import TabordionAccordion from './tabordion-accordion'
import TaboridonTabs from './tabordion-tabs'

export type TabordionItemType = {
  id?: string
  title: JSX.Element | string
  selectTitle?: string
  selectValue?: string
  content: JSX.Element | null
  href?: string
  noContentText?: JSX.Element | string
  scroll?: boolean
  legacyBehavior?: boolean
}

export enum MobileLayoutType {
  ACCORDION = 'ACCORDION',
  SELECT = 'SELECT',
  DROPDOWN = 'DROPDOWN',
}

export type Props = {
  items: TabordionItemType[]
  mega?: boolean
  mobileLayout?: MobileLayoutType
  links?: boolean
  breakpoint?: Breakpoints
  noContentPadding?: boolean
  extState?: boolean
  extActiveTabs?: number[]
  setExtActiveTabs?: (arg0: number[]) => void
  tabsClassName?: string
  accordionClassName?: string
  accordionContainerClassName?: string
  accordionFirstandLastBorder?: boolean
  selectClassName?: string
  selectItemClassName?: string
  dropdownClassName?: string
  onClickOverride?: boolean
  onClickOverrideFunction?: (arg0: number) => void
  onTabChange?: (tab: number) => void
  vertical?: boolean
  tabWidth?: string
  onTabClick?: (tabNumber: number) => void
  id?: string
  sticky?: boolean
  useExtToggleItem?: boolean
}

export default function Tabordion({
  items,
  mega = false,
  mobileLayout = MobileLayoutType.ACCORDION,
  links = false,
  breakpoint = Breakpoints.MD,
  noContentPadding = false,
  extState = false,
  extActiveTabs = [0],
  setExtActiveTabs,
  tabsClassName,
  accordionClassName,
  accordionContainerClassName,
  accordionFirstandLastBorder = false,
  selectClassName,
  selectItemClassName,
  dropdownClassName,
  onClickOverride,
  onClickOverrideFunction,
  onTabChange,
  vertical = false,
  tabWidth = 'auto',
  onTabClick,
  useExtToggleItem,
  sticky,
  id = '',
}: Props) {
  const { windowSize: viewportSize, isSmallScreen } = useResponsive()
  const [activeTabs, setActiveTabs] = useState(extActiveTabs)
  const [isTabs, setIsTabs] = useState(!isSmallScreen)

  useEffect(() => {
    setIsTabs(viewportSize.width >= breakpoint)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportSize])

  // If an external tab-state is included, keep it synced
  useEffect(() => {
    if (extState && setExtActiveTabs) {
      if (!isEqual(activeTabs, extActiveTabs)) {
        setExtActiveTabs(activeTabs)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabs])

  useEffect(() => {
    if (extState) {
      const timeout = setTimeout(() => {
        if (!isEqual(activeTabs, extActiveTabs)) {
          setActiveTabs(extActiveTabs)
        }
      }, 0)

      return () => {
        clearTimeout(timeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extActiveTabs])

  useEffect(() => {
    // When going from accordion to tab-menu: set only one item to active
    if (isTabs) {
      !activeTabs.length
        ? setActiveTabs(extActiveTabs.length ? extActiveTabs : [0])
        : setActiveTabs(extActiveTabs)
    }
    // When going from tab-menu to accordion: deactivate all
    else if (mobileLayout === MobileLayoutType.ACCORDION) {
      setActiveTabs(extActiveTabs.length ? extActiveTabs : [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabs])

  const toggleItem = (tab: number) => {
    if (!onClickOverride) {
      if (!isTabs) {
        if (mobileLayout === MobileLayoutType.ACCORDION) {
          // if accordion
          // If clicked accordion is already activated
          if (activeTabs.includes(tab)) {
            // Deactivate clicked accordion
            setActiveTabs(activeTabs.filter(t => t !== tab))
          } else {
            // Set the clicked accordion to active
            setActiveTabs([...activeTabs, tab])
          }
        } else {
          // if select
          setActiveTabs([tab])
        }
      } else {
        setActiveTabs([tab])
      }
    } else if (onClickOverrideFunction) {
      onClickOverrideFunction(tab)
    }

    if (onTabChange) {
      onTabChange(tab)
    }
  }

  return (
    <TabordionTypes
      isTabs={isTabs}
      mega={mega}
      tabsClassName={tabsClassName}
      vertical={vertical}
      onTabClick={onTabClick}
      noContentPadding={noContentPadding}
      items={items}
      id={id}
      activeTabs={activeTabs}
      sticky={sticky}
      tabWidth={tabWidth}
      links={links}
      useExtToggleItem={useExtToggleItem}
      toggleItem={toggleItem}
      selectClassName={selectClassName}
      dropdownClassName={dropdownClassName}
      selectItemClassName={selectItemClassName}
      accordionContainerClassName={accordionContainerClassName}
      accordionClassName={accordionClassName}
      accordionFirstandLastBorder={accordionFirstandLastBorder}
      mobileLayout={mobileLayout}
    />
  )
}

export const TabordionTypes = ({
  isTabs,
  activeTabs,
  toggleItem = () => {},
  items,
  mega = false,
  mobileLayout = MobileLayoutType.ACCORDION,
  links = false,
  noContentPadding = false,
  tabsClassName,
  accordionClassName,
  accordionContainerClassName,
  accordionFirstandLastBorder = false,
  selectClassName,
  selectItemClassName,
  dropdownClassName,
  vertical = false,
  tabWidth = 'auto',
  onTabClick,
  useExtToggleItem,
  sticky,
  id = '',
}: Props & { isTabs?: boolean; activeTabs: number[]; toggleItem?: (tab: number) => void }) => {
  const tabordionId = useUniqueId()

  const tabTypeClassName = !mega ? 'c-tabs' : 'c-mega-tabs'
  if (isTabs) {
    return (
      <TaboridonTabs
        id={id}
        tabTypeClassName={tabTypeClassName}
        tabsClassName={tabsClassName}
        vertical={vertical}
        mega={mega}
        noContentPadding={noContentPadding}
        items={items}
        activeTabs={activeTabs}
        tabordionId={tabordionId}
        sticky={sticky}
        tabWidth={tabWidth}
        links={links}
        onTabClick={onTabClick}
        toggleItem={useExtToggleItem ? () => {} : toggleItem}
      />
    )
  } else if (mobileLayout === MobileLayoutType.ACCORDION) {
    return (
      <TabordionAccordion
        id={id}
        items={items}
        accordionContainerClassName={accordionContainerClassName}
        activeTabs={activeTabs}
        toggleItem={useExtToggleItem ? () => {} : toggleItem}
        noContentPadding={noContentPadding}
        accordionClassName={accordionClassName}
        accordionFirstandLastBorder={accordionFirstandLastBorder}
      />
    )
  }
  return (
    <TabordionDropDown
      mobileLayout={mobileLayout}
      items={items}
      selectClassName={selectClassName}
      toggleItem={useExtToggleItem ? () => {} : toggleItem}
      activeTabs={activeTabs}
      links={links}
      dropdownClassName={dropdownClassName}
      selectItemClassName={selectItemClassName}
    />
  )
}
